// Types for Lead Import/Export functionality

export interface ImportPreviewResponse {
  success: boolean;
  importJobId: string;
  fileName: string;
  headers: string[];
  sampleRows: Record<string, string>[];
  totalRows: number;
  suggestedMapping: {
    csvColumn: string;
    leadField: string;
    confidence: number;
  }[];
}

export interface ImportStartRequest {
  importJobId: string;
  fieldMapping: Record<string, string>;
  duplicateMode: 'SKIP' | 'UPDATE' | 'CREATE_NEW';
  defaultValues?: {
    assignedToId?: string;
    priority?: string;
    stage?: string;
  };
}

export interface ImportJob {
  id: string;
  status: 'PENDING' | 'VALIDATING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  fileName?: string;
  totalRows: number;
  processedRows: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  createdAt: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface ImportError {
  id: string;
  rowNumber: number;
  fieldName?: string;
  errorType: string;
  message: string;
  rawData?: Record<string, string>;
}

export interface ExportConfig {
  format: 'csv' | 'xlsx';
  columns: string[];
  filters?: {
    stage?: string[];
    priority?: string[];
    assignedTo?: string[];
    createdAfter?: string;
    createdBefore?: string;
    minValue?: number;
    maxValue?: number;
  };
}

export interface ExportJob {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  format: 'csv' | 'xlsx';
  totalRecords: number;
  downloadUrl?: string;
  createdAt: string;
  completedAt?: string;
}

// Available lead fields for mapping
export const LEAD_FIELDS = [
  { value: 'name', label: 'Lead Name', required: true },
  { value: 'company', label: 'Company', required: true },
  { value: 'email', label: 'Email', required: false },
  { value: 'phoneNumber', label: 'Phone Number', required: false },
  { value: 'contactPerson', label: 'Contact Person', required: false },
  { value: 'industry', label: 'Industry', required: false },
  { value: 'companySize', label: 'Company Size', required: false },
  { value: 'location', label: 'Location', required: false },
  { value: 'linkedinUrl', label: 'LinkedIn URL', required: false },
  { value: 'priority', label: 'Priority', required: false },
  { value: 'stage', label: 'Stage', required: false },
  { value: 'dealValue', label: 'Deal Value', required: false },
  { value: 'expectedClose', label: 'Expected Close Date', required: false },
  { value: '', label: '-- Skip this column --', required: false },
] as const;

// Export column groups
export const EXPORT_COLUMN_GROUPS = {
  basic: {
    label: 'Basic Information',
    columns: [
      { value: 'name', label: 'Lead Name' },
      { value: 'company', label: 'Company' },
      { value: 'contactPerson', label: 'Contact Person' },
    ],
  },
  contact: {
    label: 'Contact Details',
    columns: [
      { value: 'email', label: 'Email' },
      { value: 'phoneNumber', label: 'Phone Number' },
      { value: 'linkedinUrl', label: 'LinkedIn URL' },
      { value: 'location', label: 'Location' },
    ],
  },
  company: {
    label: 'Company Details',
    columns: [
      { value: 'industry', label: 'Industry' },
      { value: 'companySize', label: 'Company Size' },
    ],
  },
  deal: {
    label: 'Deal Information',
    columns: [
      { value: 'stage', label: 'Pipeline Stage' },
      { value: 'priority', label: 'Priority' },
      { value: 'dealValue', label: 'Deal Value' },
      { value: 'expectedClose', label: 'Expected Close Date' },
    ],
  },
  metadata: {
    label: 'Metadata',
    columns: [
      { value: 'assignedToUser', label: 'Assigned To' },
      { value: 'createdAt', label: 'Created Date' },
      { value: 'updatedAt', label: 'Updated Date' },
      { value: 'lastActivity', label: 'Last Activity' },
    ],
  },
};
